import { useState, useRef } from "react";
import { Upload, X, Star, Loader2, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploader({ images = [], onChange, label = "Images", accept = "image/*" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const upload = async (files) => {
    setBusy(true);
    const next = [...images];
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push({ url: `${BACKEND_URL}${res.data.url}`, caption: "", alt: file.name, order: next.length, is_featured: next.length === 0 });
      } catch (e) {
        toast.error("Upload failed for " + file.name);
      }
    }
    onChange(next);
    setBusy(false);
  };

  const remove = (i) => {
    const next = images.filter((_, idx) => idx !== i).map((im, idx) => ({ ...im, order: idx }));
    if (next.length && !next.some((im) => im.is_featured)) next[0].is_featured = true;
    onChange(next);
  };
  const setFeatured = (i) => onChange(images.map((im, idx) => ({ ...im, is_featured: idx === i })));
  const move = (i) => { onChange([images[i], ...images.filter((_, idx) => idx !== i)].map((im, idx) => ({ ...im, order: idx }))); };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-[#111827]">{label}</label>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#C81D35]" />}
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-[#C81D35] transition-colors"
        data-testid="image-dropzone"
      >
        <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Drag & drop or click to upload</p>
        <input ref={inputRef} type="file" accept={accept} multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="image-input" />
      </div>

      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-border aspect-[4/3]" data-testid={`uploaded-image-${i}`}>
              <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
              {img.is_featured && <span className="absolute top-1 left-1 bg-[#C81D35] text-white text-[10px] px-1.5 py-0.5 rounded-full">Featured</span>}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button type="button" onClick={() => setFeatured(i)} title="Set featured" className="p-1.5 bg-white rounded-full"><Star className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i)} title="Move to front" className="p-1.5 bg-white rounded-full"><GripVertical className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(i)} title="Remove" className="p-1.5 bg-white rounded-full"><X className="w-3.5 h-3.5 text-red-600" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
